import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  Shield,
  ShieldCheck,
  RefreshCw,
  Zap,
  Sliders,
  Percent,
  Lock,
  Building2,
  Receipt,
  AlertCircle,
  Save,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminHeaders } from '../../lib/supabaseService';

interface AdminSubscriptionsSectionProps {
  subData: any;
  paystackConfig: any;
  onRefresh: () => void;
  isLoading: boolean;
}

export const AdminSubscriptionsSection: React.FC<AdminSubscriptionsSectionProps> = ({
  subData,
  paystackConfig,
  onRefresh,
  isLoading,
}) => {
  const { currentUser } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'marketplace_ledger' | 'commission_settings' | 'payouts'>('marketplace_ledger');

  // Platform commission state (0% - 30%)
  const [commissionPct, setCommissionPct] = useState<number>(10);
  const [commissionReason, setCommissionReason] = useState<string>('Standard JobMatch NG marketplace operational fee');
  const [isUpdatingCommission, setIsUpdatingCommission] = useState(false);
  const [commissionMessage, setCommissionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Financial summary data
  const [financials, setFinancials] = useState<{
    totalGrossVolume: number;
    totalJobMatchCommissions: number;
    totalProfessionalDisbursements: number;
    pendingEscrowVolume: number;
    currentCommissionPercentage: number;
    successfulTransactionsCount: number;
    recentTransactions: any[];
    payoutRequests: any[];
  }>({
    totalGrossVolume: 0,
    totalJobMatchCommissions: 0,
    totalProfessionalDisbursements: 0,
    pendingEscrowVolume: 0,
    currentCommissionPercentage: 10,
    successfulTransactionsCount: 0,
    recentTransactions: [],
    payoutRequests: [],
  });

  const loadAdminFinancials = async () => {
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/financials-overview', { headers });
      if (res.status === 401 || res.status === 403) {
        console.warn('Unauthorized or forbidden admin financials request.');
        return;
      }
      const data = await res.json();
      if (data.success && data.financials) {
        const raw = data.financials;
        setFinancials({
          totalGrossVolume: raw.totalCustomerPayments ?? raw.totalGrossVolume ?? 0,
          totalJobMatchCommissions: raw.totalJobMatchCommissions ?? 0,
          totalProfessionalDisbursements: raw.totalPayouts ?? raw.totalProfessionalDisbursements ?? 0,
          pendingEscrowVolume: raw.totalProfessionalEarnings ? Math.max(0, raw.totalProfessionalEarnings - (raw.totalPayouts || 0)) : (raw.pendingEscrowVolume ?? 0),
          currentCommissionPercentage: raw.currentCommissionPercentage ?? 10,
          successfulTransactionsCount: raw.successfulTransactionsCount ?? 0,
          recentTransactions: raw.recentTransactions ?? [],
          payoutRequests: raw.pendingPayoutsList ?? raw.payoutRequests ?? [],
        });
        setCommissionPct(raw.currentCommissionPercentage || 10);
      }
    } catch (err) {
      console.warn('Error loading admin financials:', err);
    }
  };

  useEffect(() => {
    loadAdminFinancials();
  }, []);

  const handleUpdateCommission = async (e: React.FormEvent) => {
    e.preventDefault();
    const val = Number(commissionPct);
    if (isNaN(val) || val < 0 || val > 30) {
      setCommissionMessage({
        type: 'error',
        text: 'Commission percentage must be between 0% and 30%.',
      });
      return;
    }

    setIsUpdatingCommission(true);
    setCommissionMessage(null);

    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/commission/update', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          percentage: val,
          adminId: currentUser?.id || 'admin_user',
          adminEmail: currentUser?.email || 'admin@jobmatch.ng',
          reason: commissionReason,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        setCommissionMessage({
          type: 'error',
          text: res.status === 401
            ? 'Unauthorized: Administrator session expired. Please sign in again.'
            : 'Forbidden: Administrator privileges required to update platform commission.',
        });
        return;
      }

      const data = await res.json();
      if (data.success) {
        setCommissionMessage({
          type: 'success',
          text: `Platform commission rate updated to ${val}%. All future job payments will use this rate.`,
        });
        await loadAdminFinancials();
      } else {
        setCommissionMessage({
          type: 'error',
          text: data.error || 'Failed to update platform commission.',
        });
      }
    } catch (err: any) {
      setCommissionMessage({
        type: 'error',
        text: err.message || 'Network error updating commission rate.',
      });
    } finally {
      setIsUpdatingCommission(false);
    }
  };

  const transactions = financials.recentTransactions || subData?.payments || [];

  const filteredTransactions = transactions.filter((tx: any) => {
    const ref = (tx.payment_reference || tx.paymentReference || '').toLowerCase();
    const jobTitle = (tx.job_title || tx.jobTitle || '').toLowerCase();
    const proId = (tx.professional_id || tx.professionalId || '').toLowerCase();
    const custId = (tx.customer_id || tx.customerId || '').toLowerCase();
    const term = searchTerm.toLowerCase();

    return !searchTerm || ref.includes(term) || jobTitle.includes(term) || proId.includes(term) || custId.includes(term);
  });

  return (
    <div id="admin-subscriptions-section" className="space-y-6 animate-fade-in">
      {/* Revenue & Paystack Health KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total GMV */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Marketplace GMV
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center font-bold">
              ₦
            </div>
          </div>
          <div className="text-2xl font-black text-white font-mono mt-3">
            ₦{(financials.totalGrossVolume || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
            <span>{financials.successfulTransactionsCount || filteredTransactions.length} Paid Jobs</span>
          </div>
        </div>

        {/* JobMatch Commission Revenue */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Platform Commission (Earned)
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold">
              <Percent className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-400 font-mono mt-3">
            ₦{(financials.totalJobMatchCommissions || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Active rate: {financials.currentCommissionPercentage || 10}% on jobs
          </div>
        </div>

        {/* Professional Payouts */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Artisan Net Earnings
            </span>
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-white font-mono mt-3">
            ₦{(financials.totalProfessionalDisbursements || 0).toLocaleString()}
          </div>
          <div className="text-xs text-purple-400 mt-1">
            90% directly disbursed to artisans
          </div>
        </div>

        {/* Paystack Gateway Status */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Paystack Gateway
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <CreditCard className="w-4 h-4" />
            </div>
          </div>
          <div className="text-sm font-bold text-white mt-3 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
            <span>Escrow & Checkout Active</span>
          </div>
          <div className="text-xs text-slate-400 mt-1 font-mono text-[11px] truncate">
            {paystackConfig?.publicKey
              ? `Key: ${paystackConfig.publicKey.slice(0, 14)}...`
              : 'Production Active'}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-4 rounded-2xl">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('marketplace_ledger')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'marketplace_ledger'
                ? 'bg-emerald-500 text-slate-950 shadow-md'
                : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            Marketplace Ledger ({filteredTransactions.length})
          </button>

          <button
            onClick={() => setActiveTab('commission_settings')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'commission_settings'
                ? 'bg-emerald-500 text-slate-950 shadow-md'
                : 'bg-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            Commission Settings ({financials.currentCommissionPercentage || 10}%)
          </button>
        </div>

        {activeTab === 'marketplace_ledger' && (
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by ref, job, customer, or pro ID..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </div>
        )}
      </div>

      {/* TAB 1: Marketplace Transactions Ledger */}
      {activeTab === 'marketplace_ledger' && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950/60 border-b border-slate-800 text-[11px] uppercase font-bold text-slate-400 tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Paystack Reference</th>
                  <th className="py-3.5 px-4">Job Title</th>
                  <th className="py-3.5 px-4">Customer</th>
                  <th className="py-3.5 px-4">Professional</th>
                  <th className="py-3.5 px-4 text-right">Gross Amount</th>
                  <th className="py-3.5 px-4 text-right">Commission (10%)</th>
                  <th className="py-3.5 px-4 text-right">Artisan Net (90%)</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-slate-500">
                      No marketplace transactions recorded yet.
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((tx: any) => {
                    const gross = Number(tx.gross_amount || tx.grossAmount || tx.amount || 0);
                    const comm = Number(tx.commission_amount || tx.commissionAmount || gross * 0.1);
                    const proNet = Number(tx.professional_amount || tx.professionalAmount || gross * 0.9);

                    return (
                      <tr key={tx.id || tx.payment_reference} className="hover:bg-slate-800/40 transition-colors">
                        <td className="py-3.5 px-4 font-mono font-medium text-slate-200">
                          {tx.payment_reference || tx.paymentReference || 'PAYSTACK-REF'}
                        </td>
                        <td className="py-3.5 px-4 text-white font-semibold">
                          {tx.job_title || tx.jobTitle || 'Verified Trade Job'}
                        </td>
                        <td className="py-3.5 px-4 text-slate-300">
                          {tx.customer_name || tx.customerName || tx.customer_id || 'Customer'}
                        </td>
                        <td className="py-3.5 px-4 text-slate-300">
                          {tx.professional_name || tx.professionalName || tx.professional_id || 'Artisan'}
                        </td>
                        <td className="py-3.5 px-4 text-right font-mono font-bold text-white">
                          ₦{gross.toLocaleString()}
                        </td>
                        <td className="py-3.5 px-4 text-right font-mono text-emerald-400 font-bold">
                          ₦{comm.toLocaleString()}
                        </td>
                        <td className="py-3.5 px-4 text-right font-mono text-slate-300">
                          ₦{proNet.toLocaleString()}
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>{tx.payment_status || tx.paymentStatus || 'success'}</span>
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                          {new Date(tx.paid_at || tx.created_at || tx.createdAt).toLocaleDateString('en-NG')}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Configurable Commission Settings */}
      {activeTab === 'commission_settings' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl max-w-2xl space-y-6">
          <div className="border-b border-slate-800 pb-5">
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <Sliders className="w-5 h-5 text-emerald-400" />
              <span>Platform Commission Settings</span>
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Configure the marketplace take-rate percentage charged on customer payments for accepted jobs.
            </p>
          </div>

          {commissionMessage && (
            <div
              className={`p-4 rounded-xl text-xs font-semibold flex items-center gap-2 ${
                commissionMessage.type === 'success'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
              }`}
            >
              {commissionMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
              )}
              <span>{commissionMessage.text}</span>
            </div>
          )}

          <form onSubmit={handleUpdateCommission} className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Commission Percentage (%)
                </label>
                <span className="text-2xl font-black text-emerald-400 font-mono">
                  {commissionPct}%
                </span>
              </div>

              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={commissionPct}
                onChange={(e) => setCommissionPct(Number(e.target.value))}
                className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />

              <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                <span>0% (Free Marketplace)</span>
                <span>10% (Default)</span>
                <span>30% (Maximum Allowed)</span>
              </div>
            </div>

            {/* Live Calculation Preview */}
            <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-2 text-xs">
              <span className="font-bold text-slate-300 block">Live Split Preview on a ₦100,000 Job:</span>
              <div className="flex justify-between text-slate-400">
                <span>Customer Invoiced Amount:</span>
                <span className="font-mono text-white">₦100,000 (100%)</span>
              </div>
              <div className="flex justify-between text-emerald-400 font-semibold">
                <span>JobMatch NG Platform Fee ({commissionPct}%):</span>
                <span className="font-mono">₦{((100000 * commissionPct) / 100).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-slate-300 font-bold border-t border-slate-800 pt-2">
                <span>Artisan Take-Home Payout ({100 - commissionPct}%):</span>
                <span className="font-mono text-emerald-400">
                  ₦{((100000 * (100 - commissionPct)) / 100).toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                Reason for Change (Audit Log Ledger)
              </label>
              <input
                type="text"
                value={commissionReason}
                onChange={(e) => setCommissionReason(e.target.value)}
                placeholder="e.g. Platform operational adjustment"
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                required
              />
            </div>

            <div className="pt-2 flex items-center justify-end">
              <button
                type="submit"
                disabled={isUpdatingCommission}
                className="px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isUpdatingCommission ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Saving Settings...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Platform Commission ({commissionPct}%)</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
